import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '../components/AdminLayout';

interface AuditLog {
  id: string;
  user_id: string | null;
  user_name: string | null;
  role: string | null;
  action: string;
  module: string | null;
  ip_address: string | null;
  device_info: string | null;
  created_at: string;
}

export default function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  const loadLogs = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (err) throw err;
      setLogs(data || []);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useRealtime<AuditLog>({
    table: 'audit_logs',
    onAny: () => {
      loadLogs();
    },
  });

  const actions = useMemo(() => [...new Set(logs.map((a) => a.action))].sort(), [logs]);
  const roles = useMemo(() => [...new Set(logs.map((a) => a.role).filter(Boolean))].sort(), [logs]);

  const filtered = useMemo(() => {
    let result = [...logs];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((a) =>
        (a.user_name || '').toLowerCase().includes(q) ||
        a.action.toLowerCase().includes(q) ||
        (a.ip_address || '').includes(q) ||
        (a.module || '').toLowerCase().includes(q)
      );
    }
    if (actionFilter) result = result.filter((a) => a.action === actionFilter);
    if (roleFilter) result = result.filter((a) => a.role === roleFilter);
    return result;
  }, [logs, search, actionFilter, roleFilter]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const roleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'organizer': return 'bg-primary-100 text-primary-700';
      case 'participant': return 'bg-accent-100 text-accent-700';
      default: return 'bg-background-200 text-foreground-500';
    }
  };

  return (
    <AuthGuard requiredRole="admin">
      <AdminLayout title="Audit Logs" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Audit Logs' }]}>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="cursor-pointer"><i className="ri-close-line"></i></button>
          </div>
        )}

        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 mb-5">
          <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between">
            <div className="relative w-full lg:w-72">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input type="text" value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} placeholder="Search logs..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
            </div>
            <div className="flex flex-wrap gap-2">
              <select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                <option value="">All Actions</option>
                {actions.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }} className="px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                <option value="">All Roles</option>
                {roles.map((r) => <option key={r} value={r} className="capitalize">{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <p className="text-xs text-foreground-400 mt-3">{filtered.length} log entry{filtered.length !== 1 ? 'ies' : ''} found</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
          </div>
        ) : (
          <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    {['User', 'Role', 'Action', 'Module', 'IP Address', 'Date & Time'].map((label) => (
                      <th key={label} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((log) => (
                    <tr key={log.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center">
                            <span className="text-xs font-bold text-foreground-500">{(log.user_name || '?')[0]}</span>
                          </div>
                          <span className="font-medium text-foreground-900">{log.user_name || 'System'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${roleColor(log.role || '')}`}>{log.role || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 text-foreground-700 max-w-xs truncate">{log.action}</td>
                      <td className="px-4 py-3 text-foreground-500 text-xs whitespace-nowrap">{log.module || '—'}</td>
                      <td className="px-4 py-3 text-foreground-500 text-xs font-mono">{log.ip_address || '—'}</td>
                      <td className="px-4 py-3 text-foreground-500 text-xs whitespace-nowrap">
                        {new Date(log.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                    </tr>
                  ))}
                  {paginated.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-foreground-400">No audit logs found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-background-200/70">
                <p className="text-xs text-foreground-400">Showing {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, filtered.length)} of {filtered.length}</p>
                <div className="flex items-center gap-1">
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer">
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button key={p} onClick={() => setCurrentPage(p)} className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors cursor-pointer ${p === currentPage ? 'bg-primary-500 text-background-50 dark:text-foreground-950' : 'text-foreground-600 hover:bg-background-100'}`}>{p}</button>
                  ))}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer">
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </AdminLayout>
    </AuthGuard>
  );
}